import BankOutlined from '@ant-design/icons/BankOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import TeamOutlined from '@ant-design/icons/TeamOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { v4 as uuid } from 'uuid'
import { blue } from '../../color'
import {
  ASSIGNEE_TYPE,
  DATE_FORMAT_LIST,
  ISO_DATE_FORMAT,
  MODES,
  PROPOSAL_STATUS,
  SHIFTS,
} from '../../constants'
import ApprovalList from '../../pages/Administrative/LeaveForm/ApproverListModal/Modal'
import { SelectList } from '../../pages/MissionCreatePage/MissionCreatePageStyled'
import commandStore from '../../stores/commandStore'
import { AvatarHasName } from '../Common/Avatar'
import RichEditor from '../RichEditor/RichEditor'
import SelectGroupPopup from '../SelectGroupPopup/SelectGroupPopup'
import SelectPeoplePopup from '../SelectPeoplePopup/SelectPeoplePopup'
import validator from './../../validator'
import ProposalStepApprove, {
  ASSIGNEE_TYPE_CONFIG,
} from './ProposalStepApprove'
import { FormButtonRow } from './SubmissionFormStyled'

const { RangePicker } = DatePicker
const { Option } = Select

const currentStep = 0

const OtherSubmissionForm = props => {
  const {
    proposalStore,
    fileStore,
    loadingAnimationStore,
    authenticationStore,
    onCloseModal,
    proposalTypeById,
    handleModalClose,
    handleDownloadAttachment,
    contentTemplateStore,
    selectPeopleStore,
    approvalTemplateStore,
    approvalTemplateList,
    form,
  } = props

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectGroupPopup,
    setIsVisibleSelectGroupPopup,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    handleOpenSelectGroupPopup,
    handleCancelSelectGroup,
  } = selectPeopleStore

  const { items, content } = contentTemplateStore

  const EDITOR_REF = useRef()

  const [fileList, setFileList] = useState([])
  const [handlerList, setHandlerList] = useState([])
  const [followerList, setFollowerList] = useState([])
  const [reasonForLeaving, setReasonForLeaving] = useState('')
  const [overtime, setOvertime] = useState('')
  const [approvalLevels, setApprovalLevels] = useState([])
  const [selectedContentKey, setSelectedContentKey] = useState(
    proposalTypeById?.contentKey || null
  )
  const [
    selectedApproveTemplateCode,
    setSelectedApproveTemplateCode,
  ] = useState('')
  const [selectedDates, setSelectedDates] = useState([])
  const [proposalTimeList, setProposalTimeList] = useState([])
  const [
    isApprovalLevelsInitialized,
    setIsApprovalLevelsInitialized,
  ] = useState(false)

  const normalizeApprovalLevels = levels => {
    return levels.map((level, index) => ({
      ...level,
      level: index, // Đảm bảo level luôn tuần tự từ 0
      name: `Cấp duyệt ${index + 1}`, // Tên vẫn bắt đầu từ 1 để hiển thị
      status: index === 0 ? 'CREATED' : index === 1 ? 'PENDING' : 'WAITING', // Level 0 = CREATED, Level 1 = PENDING
    }))
  }

  // Helper function to create default approval levels
  const createDefaultLevels = useCallback(
    () => [
      {
        status: 'CREATED',
        allow_edit: true,
        level: 1,
        name: 'Cấp duyệt 1',
        username: '',
        user: null,
        code: uuid(),
      },
      {
        status: 'PENDING',
        allow_edit: true,
        level: 2,
        name: 'Cấp duyệt 2',
        username: '',
        user: null,
        code: uuid(),
      },
    ],
    []
  )

  const clearSelectPeopleStore = useCallback(async () => {
    await selectPeopleStore.clearSelected()
  }, [selectPeopleStore])

  // Lấy approvalLevels
  const getApprovalLevelTemplate = useCallback(
    async code => {
      try {
        const response = await approvalTemplateStore.getApprovalLevelTemplateByATCode(
          code
        )
        const approvalLevelsChange = response.data.map(
          (approvalLevel, index) => ({
            status: index === 0 ? 'CREATED' : 'WAITING',
            allow_edit: approvalLevel.allow_edit,
            code: approvalLevel.code,
            isDeleted: approvalLevel.isDeleted,
            level: approvalLevel.level,
            name: approvalLevel.name,
            username: approvalLevel.username,
            user: approvalLevel.username
              ? { username: approvalLevel.username }
              : null,
          })
        )
        setApprovalLevels(approvalLevelsChange)
        setIsApprovalLevelsInitialized(true)
      } catch (err) {
        console.log(err)
        message.error(err?.vi || 'Đã có lỗi xảy ra!')
        // Set default levels on error
        const defaultLevels = createDefaultLevels()
        setApprovalLevels(defaultLevels)
        setIsApprovalLevelsInitialized(true)
      }
    },
    [approvalTemplateStore, createDefaultLevels]
  )

  // tìm item cuối cùng thỏa mãn điều kiện
  const findLastTemplate = useCallback((array, conditionFunction) => {
    if (!array?.length) return undefined
    for (let i = array.length - 1; i >= 0; i--) {
      if (conditionFunction(array[i])) {
        return array[i]
      }
    }
    return undefined
  }, [])

  const findLastTemplateV2 = useCallback(array => {
    if (!array?.length) return undefined
    for (let i = array.length - 1; i >= 0; i--) {
      if (!array[i].isDeleted) {
        return array[i]
      }
    }
    return undefined
  }, [])

  // FIXED: Initialize approval levels only once when component mounts or dependencies change
  useEffect(() => {
    if (isApprovalLevelsInitialized) return

    const initializeApprovalLevels = async () => {
      try {
        if (proposalTypeById?.approvalTemplateDTO == null) {
          if (approvalTemplateList?.length > 0) {
            const template = findLastTemplateV2(approvalTemplateList)
            if (template) {
              setSelectedApproveTemplateCode(template.code)
              await getApprovalLevelTemplate(template.code)
            } else {
              const defaultLevels = createDefaultLevels()
              setApprovalLevels(defaultLevels)
              setIsApprovalLevelsInitialized(true)
            }
          } else {
            const defaultLevels = createDefaultLevels()
            setApprovalLevels(defaultLevels)
            setIsApprovalLevelsInitialized(true)
          }
        } else if (
          approvalTemplateList?.some(
            item => item.code === proposalTypeById?.approvalTemplateDTO?.code
          )
        ) {
          setSelectedApproveTemplateCode(
            proposalTypeById.approvalTemplateDTO.code
          )
          await getApprovalLevelTemplate(
            proposalTypeById.approvalTemplateDTO.code
          )
        } else {
          const template = findLastTemplate(
            approvalTemplateList,
            template => template.approval_levels?.length === 3
          )
          if (template) {
            setSelectedApproveTemplateCode(template.code)
            await getApprovalLevelTemplate(template.code)
          } else {
            const defaultLevels = createDefaultLevels()
            setApprovalLevels(defaultLevels)
            setIsApprovalLevelsInitialized(true)
          }
        }
      } catch (error) {
        console.error('Error initializing approval levels:', error)
        const defaultLevels = createDefaultLevels()
        setApprovalLevels(defaultLevels)
        setIsApprovalLevelsInitialized(true)
      }
    }

    initializeApprovalLevels()
  }, [
    approvalTemplateList,
    proposalTypeById?.approvalTemplateDTO,
    isApprovalLevelsInitialized,
    getApprovalLevelTemplate,
    findLastTemplate,
    findLastTemplateV2,
    createDefaultLevels,
  ])

  // FIXED: Simplified handler/follower list updates with proper comparison
  useEffect(() => {
    const newHandlerList = [
      ...(Array.isArray(selectUserData[ASSIGNEE_TYPE.HANDLER])
        ? selectUserData[ASSIGNEE_TYPE.HANDLER]
        : []),
      ...(Array.isArray(selectDepartmentData[ASSIGNEE_TYPE.HANDLER])
        ? selectDepartmentData[ASSIGNEE_TYPE.HANDLER]
        : []),
      ...(Array.isArray(selectGroupData[ASSIGNEE_TYPE.HANDLER])
        ? selectGroupData[ASSIGNEE_TYPE.HANDLER]
        : []),
    ]

    const newFollowerList = [
      ...(Array.isArray(selectUserData[ASSIGNEE_TYPE.FOLLOWER])
        ? selectUserData[ASSIGNEE_TYPE.FOLLOWER]
        : []),
      ...(Array.isArray(selectDepartmentData[ASSIGNEE_TYPE.FOLLOWER])
        ? selectDepartmentData[ASSIGNEE_TYPE.FOLLOWER]
        : []),
      ...(Array.isArray(selectGroupData[ASSIGNEE_TYPE.FOLLOWER])
        ? selectGroupData[ASSIGNEE_TYPE.FOLLOWER]
        : []),
    ]

    // Only update if arrays are actually different
    const handlerListString = JSON.stringify(handlerList.map(h => h.id).sort())
    const newHandlerListString = JSON.stringify(
      newHandlerList.map(h => h.id).sort()
    )
    const followerListString = JSON.stringify(
      followerList.map(f => f.id).sort()
    )
    const newFollowerListString = JSON.stringify(
      newFollowerList.map(f => f.id).sort()
    )

    if (handlerListString !== newHandlerListString) {
      setHandlerList(newHandlerList)
    }

    if (followerListString !== newFollowerListString) {
      setFollowerList(newFollowerList)
    }
  }, [
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    handlerList,
    followerList,
  ])

  /** Xử lý thay đổi file upload */
  const handleChangeFile = useCallback(info => {
    const filteredFileList = info.fileList.filter(
      (elem, index, fileArray) =>
        fileArray.findIndex(file => file.name === elem.name) === index
    )
    setFileList(filteredFileList)
  }, [])

  /** Xử lý ghép API tạo phiếu trình nghỉ phép */
  const handleCreateOther = useCallback(
    async data => {
      try {
        await proposalStore.createOther(data)
        proposalStore.setActiveTabProposal('')
        proposalStore.clearFilterProposal()
        proposalStore.resetFormProposalType()
        onCloseModal()
        await proposalStore.getProposals()
        message.success('Tạo mới thành công!')
      } catch (error) {
        console.log(error)
        message.error(error.vi || 'Có lỗi xảy ra, vui lòng thử lại!')
      }
    },
    [onCloseModal, proposalStore]
  )

  const handleCloseAndReset = useCallback(() => {
    onCloseModal()
    form.setFieldsValue({
      title: null,
      type_of_leave: null,
    })
    setReasonForLeaving('')
    setOvertime('')
    setIsApprovalLevelsInitialized(false)
    setApprovalLevels([])
    clearSelectPeopleStore()
    handleModalClose()
  }, [onCloseModal, form, handleModalClose, clearSelectPeopleStore])

  const onFinish = useCallback(
    async values => {
      let hasWarning = false
      const storeApprovalLevels =
        proposalStore.defaultApprovalConfigUpdate.approvalLevels

      const hasEmptyApprovalLevel = storeApprovalLevels.some(
        item => !item?.username || item.username.trim() === ''
      )

      if (hasEmptyApprovalLevel) {
        message.warning('Hãy chọn đủ cấp duyệt!')
        hasWarning = true
      }
      if (values.title.trim() == '') {
        message.warning(`Hãy nhập tiêu đề!`)
        hasWarning = true
      }
      if (hasWarning) return

      let fromDate =
        values?.date_picker && values?.date_picker.length == 2
          ? moment(values?.date_picker[0]).format(ISO_DATE_FORMAT)
          : ''
      let toDate =
        values?.date_picker && values?.date_picker.length == 2
          ? moment(values?.date_picker[1]).format(ISO_DATE_FORMAT)
          : ''

      // Normalize approval levels trước khi submit để đảm bảo level tuần tự
      const normalizedApprovalLevels = normalizeApprovalLevels(
        storeApprovalLevels
      )

      const approvalLevelsForSubmit = normalizedApprovalLevels.map(
        (approvalLevel, index) => ({
          status: index === 0 ? 'CREATED' : index === 1 ? 'PENDING' : 'WAITING', // Level 0 = CREATED, Level 1 = PENDING, còn lại = WAITING
          allow_edit: approvalLevel.allow_edit,
          code: approvalLevel.code,
          isDeleted: approvalLevel.is_deleted,
          level: approvalLevel.level,
          name: approvalLevel.name,
          username: approvalLevel.username,
        })
      )

      const submitValues = {
        ...values,
        title: values.title.replace(/\s+/g, ' ').trim(),
        proposal_type: proposalTypeById.id,
        followers: followerList.length > 0 ? followerList.map(el => el.id) : [],
        reason: EDITOR_REF.current.editor.getData(),
        reason_for_leaving: reasonForLeaving,
        type_of_leave: values?.type_of_leave,
        from_date: fromDate,
        to_date: toDate,
        proposalTimeList: proposalTimeList,
        overtime: overtime,
        // Sử dụng approval levels đã được normalize
        approval_levels: approvalLevelsForSubmit.map((item, index) => ({
          level: index,
          name: `Cấp duyệt ${index + 1}`,
          username: item.username,
          allow_edit: item.allow_edit,
          status:
            index === 0
              ? 'CREATED' // Level 0 luôn luôn là CREATED
              : index === 1
              ? PROPOSAL_STATUS.PENDING // Level 1 là PENDING (cấp đầu tiên cần duyệt)
              : 'WAITING', // Các level khác là WAITING
        })),
      }

      // Rest of the submit logic remains the same
      const uploadFileList =
        submitValues.file_ids && submitValues.file_ids.fileList
          ? submitValues.file_ids.fileList.map(file => file.originFileObj)
          : []
      const batchUploadArr = []
      uploadFileList.forEach(file => {
        const formData = new FormData()
        formData.append('file', file)
        batchUploadArr.push(fileStore.uploadFile(formData))
      })
      let uploadedFiles = []
      try {
        loadingAnimationStore.showSpinner(true)
        const response = await Promise.all(batchUploadArr)
        uploadedFiles = response.map(response => response.data.file_id)
        submitValues.file_ids = uploadedFiles

        await handleCreateOther(submitValues)
        handleCloseAndReset()
        setFileList([])
        form.resetFields()
        loadingAnimationStore.showSpinner(false)
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Có lỗi xảy ra')
      } finally {
        loadingAnimationStore.showSpinner(false)
        commandStore.getSideMenuCounter([
          'WORK',
          'PROPOSAL',
          'PROPOSAL_ADVANCE',
          'INTERNAL_MESSAGE',
          'INCOMING',
        ])
      }
    },
    [
      handleCloseAndReset,
      handleCreateOther,
      approvalLevels,
      proposalTypeById,
      followerList,
      reasonForLeaving,
      overtime,
      proposalTimeList,
      fileStore,
      loadingAnimationStore,
      commandStore,
      form,
    ]
  )

  // FIXED: Reset state when proposal type changes
  useEffect(() => {
    contentTemplateStore.load()
    contentTemplateStore.getDetails(proposalTypeById.contentKey)
    setSelectedContentKey(proposalTypeById.contentKey)
    setReasonForLeaving('')
    setOvertime('')
    form.setFieldsValue({
      type_of_leave: null,
      date_picker: null,
    })
    setProposalTimeList([])
    setIsApprovalLevelsInitialized(false)
    return () => {
      contentTemplateStore.clear()
    }
  }, [
    proposalTypeById?.id,
    proposalTypeById?.contentKey,
    form,
    contentTemplateStore,
  ])

  const onChangeApprovalTemplate = useCallback(
    async value => {
      setSelectedApproveTemplateCode(value)
      await clearSelectPeopleStore()
      await getApprovalLevelTemplate(value)
    },
    [getApprovalLevelTemplate, clearSelectPeopleStore]
  )

  const handleShiftChange = useCallback((value, recordKey) => {
    setProposalTimeList(prevList =>
      prevList.map(day =>
        day.key === recordKey ? { ...day, shift: value } : day
      )
    )
  }, [])

  const handleModeChange = useCallback((value, recordKey) => {
    let modeTypeValue = 0

    if (value === MODES.WOMEN.key) {
      modeTypeValue = 0.5
    } else if (value === MODES.CHILD_CARE.key) {
      modeTypeValue = 1
    }

    setProposalTimeList(prevList =>
      prevList.map(day =>
        day.key === recordKey
          ? { ...day, mode: value, mode_type: modeTypeValue }
          : day
      )
    )
  }, [])

  const handleModeTypeChange = useCallback((value, recordKey) => {
    setProposalTimeList(prevList =>
      prevList.map(day =>
        day.key === recordKey ? { ...day, mode_type: value } : day
      )
    )
  }, [])

  const handleNoteChange = useCallback((value, recordKey) => {
    setProposalTimeList(prevList =>
      prevList.map(day =>
        day.key === recordKey ? { ...day, note: value } : day
      )
    )
  }, [])

  const handleDateChange = useCallback((value, dates) => {
    setSelectedDates(dates)
  }, [])

  const getRange = useCallback(
    (startDate, endDate) => {
      let fromDate = moment(startDate, 'DD/MM/YYYY')
      let toDate = moment(endDate, 'DD/MM/YYYY')
      let diff = toDate.diff(fromDate, 'days')
      let range = []

      for (let i = 0; i <= diff; i++) {
        let currentDay = moment(startDate, 'DD/MM/YYYY').add(i, 'days')
        if (proposalTypeById?.id === 'OVERTIME_NORMAL') {
          range.push(currentDay)
        } else {
          if (currentDay.day() !== 6 && currentDay.day() !== 0) {
            range.push(currentDay)
          }
        }
      }

      return range
    },
    [proposalTypeById?.id]
  )

  const generateWorkDays = useCallback(() => {
    const workDays = []
    if (!selectedDates || selectedDates.length < 2) {
      setProposalTimeList([])
      return
    }

    const dateRange = getRange(selectedDates[0], selectedDates[1])
    if (dateRange?.length > 0) {
      if (proposalTypeById?.id === 'LEAVE_ABNORMAL') {
        dateRange.forEach((date, index) => {
          workDays.push({
            key: index.toString(),
            date: date,
            shift: 'morning',
            mode: 'normal',
            mode_type: 0,
            note: null,
          })
        })
      } else {
        dateRange.forEach((date, index) => {
          workDays.push({
            key: index.toString(),
            date: date,
            shift: 'allDay',
            mode: 'normal',
            note: null,
          })
        })
      }
      setProposalTimeList(workDays)
    } else {
      setProposalTimeList([])
    }
  }, [selectedDates, getRange, proposalTypeById?.id])

  useEffect(() => {
    if (selectedDates?.length === 2) {
      generateWorkDays()
    } else if (selectedDates?.length === 0) {
      setProposalTimeList([])
    }
  }, [selectedDates, generateWorkDays])

  const columns = useMemo(
    () => [
      {
        title: 'Ngày làm việc',
        dataIndex: 'date',
        key: 'date',
        render: text => <span>{moment(text).format('DD/MM/YYYY')}</span>,
      },
      {
        title: 'Buổi làm việc',
        dataIndex: 'shift',
        key: 'shift',
        render: (text, record) => (
          <Select
            style={{
              width: '120px',
            }}
            defaultValue={text}
            onChange={value => handleShiftChange(value, record.key)}>
            <Option value={SHIFTS.ALL_DAY.key}>{SHIFTS.ALL_DAY.value}</Option>
            <Option value={SHIFTS.MORNING.key}>{SHIFTS.MORNING.value}</Option>
            <Option value={SHIFTS.AFTERNOON.key}>
              {SHIFTS.AFTERNOON.value}
            </Option>
            <Option value={SHIFTS.EMPTY.key}></Option>
          </Select>
        ),
      },
      ...(proposalTypeById.id !== 'BUSINESS_TRIP' &&
      proposalTypeById.id !== 'OVERTIME_NORMAL'
        ? [
            {
              title: 'Loại nghỉ phép',
              dataIndex: 'mode',
              key: 'mode',
              render: (text, record) => (
                <Select
                  style={{
                    width: '120px',
                  }}
                  defaultValue={text}
                  onChange={value => handleModeChange(value, record.key)}>
                  <Option value={MODES.NORMAL.key}>{MODES.NORMAL.value}</Option>
                  <Option value={MODES.SPECIAL.key}>
                    {MODES.SPECIAL.value}
                  </Option>
                </Select>
              ),
            },
          ]
        : []),
      ...(proposalTypeById.id === 'OVERTIME_NORMAL'
        ? [
            {
              title: 'Giờ',
              dataIndex: 'mode_type',
              key: 'mode_type',
              width: '150px',
              render: (text, record) => {
                return (
                  <InputNumber
                    style={{ width: '90px' }}
                    defaultValue={0}
                    min={0}
                    onChange={value => handleModeTypeChange(value, record.key)}
                  />
                )
              },
            },
          ]
        : []),
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        key: 'note',
        render: (text, record) => (
          <Input
            value={text}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              height: '32px',
              backgroundColor: '#fff',
            }}
            onChange={e => handleNoteChange(e.target.value, record.key)}
            placeholder="Nhập ghi chú"
          />
        ),
      },
    ],
    [
      handleShiftChange,
      handleModeChange,
      handleModeTypeChange,
      handleNoteChange,
      proposalTypeById.id,
    ]
  )

  const leaveAbnormalColumns = useMemo(
    () => [
      {
        title: 'Ngày làm việc',
        dataIndex: 'date',
        key: 'date',
        render: text => <span>{moment(text).format('DD/MM/YYYY')}</span>,
      },
      {
        title: 'Buổi làm việc',
        dataIndex: 'shift',
        key: 'shift',
        render: (text, record) => (
          <Select
            style={{
              width: '120px',
            }}
            defaultValue={text}
            onChange={value => handleShiftChange(value, record.key)}>
            <Option value={SHIFTS.MORNING.key}>{SHIFTS.MORNING.value}</Option>
            <Option value={SHIFTS.AFTERNOON.key}>
              {SHIFTS.AFTERNOON.value}
            </Option>
            <Option value={SHIFTS.EMPTY.key}></Option>
          </Select>
        ),
      },
      {
        title: 'Loại nghỉ phép',
        dataIndex: 'mode',
        key: 'mode',
        render: (text, record) => (
          <Select
            style={{
              width: '160px',
            }}
            defaultValue={text}
            onChange={value => handleModeChange(value, record.key)}>
            <Option value={MODES.NORMAL.key}>{MODES.NORMAL.value}</Option>
            <Option value={MODES.WOMEN.key}>{MODES.WOMEN.value}</Option>
            <Option value={MODES.CHILD_CARE.key}>
              {MODES.CHILD_CARE.value}
            </Option>
            <Option value={MODES.WORK_REASON.key}>
              {MODES.WORK_REASON.value}
            </Option>
            <Option value={MODES.BY_REGIME.key}>{MODES.BY_REGIME.value}</Option>
          </Select>
        ),
      },
      {
        title: 'Thời gian (giờ)',
        dataIndex: 'mode_type',
        key: 'mode_type',
        width: '150px',
        render: (text, record) => {
          switch (record.mode) {
            case MODES.NORMAL.key:
            case MODES.WORK_REASON.key:
            case MODES.BY_REGIME.key:
              return (
                <InputNumber
                  style={{ width: '90px' }}
                  defaultValue={0}
                  min={0}
                  max={8}
                  onChange={value => handleModeTypeChange(value, record.key)}
                />
              )
            case MODES.WOMEN.key:
              return <span>0.5</span>
            case MODES.CHILD_CARE.key:
              return <span>1</span>
            default:
              return null
          }
        },
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        key: 'note',
        width: '250px',
        render: (text, record) => (
          <input
            value={text}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              height: '32px',
              backgroundColor: '#fff',
            }}
            onChange={e => handleNoteChange(e.target.value, record.key)}
            placeholder="Nhập ghi chú"
          />
        ),
      },
    ],
    [
      handleShiftChange,
      handleModeChange,
      handleModeTypeChange,
      handleNoteChange,
    ]
  )

  const approvalTemplateListTypeProposal = useMemo(
    () =>
      approvalTemplateList?.filter(item => item.templateType === 'proposal'),
    [approvalTemplateList]
  )

  const backupCompleteUserData = useCallback(() => {
    const backup = {}

    approvalLevels.forEach((level, index) => {
      if (level.allow_edit) {
        const levelAllowEditList = approvalLevels.filter(
          item => item.allow_edit
        )
        const assignTypeIndex = levelAllowEditList.findIndex(
          item => item.code === level.code
        )
        const assigneeType =
          assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
            ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
            : `DYNAMIC_LEVEL_${assignTypeIndex}`

        // Ưu tiên data từ selectUserData, fallback về approvalLevels
        let userData = null
        let username = ''

        if (
          selectUserData[assigneeType] &&
          selectUserData[assigneeType].length > 0
        ) {
          // Data từ selectUserData (vừa chọn)
          userData = selectUserData[assigneeType][0]
          username = userData.id
        } else if (level.username && level.user) {
          // Data có sẵn trong approvalLevels (từ template hoặc đã lưu)
          userData = {
            id: level.username,
            full_name: level.user.full_name,
            image_id: level.user.image_id,
            username: level.username,
          }
          username = level.username
        }

        if (userData && username) {
          backup[level.code] = {
            username: username,
            userData: userData,
            assigneeType: assigneeType,
            levelInfo: {
              name: level.name,
              level: level.level,
            },
          }
        }
      }
    })

    return backup
  }, [approvalLevels, selectUserData])

  // Helper function để restore dữ liệu với mapping mới
  const restoreCompleteUserData = useCallback(
    async (backup, newApprovalLevels) => {
      try {
        // Clear toàn bộ selectUserData
        await selectPeopleStore.clearSelected()

        // Normalize levels trước khi restore
        const normalizedLevels = normalizeApprovalLevels(newApprovalLevels)

        // Cập nhật approvalLevels với dữ liệu từ backup trước
        const levelsWithRestoredData = normalizedLevels.map(level => {
          if (level.allow_edit && backup[level.code]) {
            const backupData = backup[level.code]
            return {
              ...level,
              username: backupData.username,
              user: {
                full_name: backupData.userData.full_name || backupData.username,
                image_id: backupData.userData.image_id,
                username: backupData.username,
                department: null,
              },
            }
          }
          return level
        })

        // Set lại approvalLevels với data đã restore và normalized
        setApprovalLevels(levelsWithRestoredData)
        proposalStore.setDefaultApprovalConfigUpdate(levelsWithRestoredData)

        // Restore vào selectUserData để UI sync
        for (const level of normalizedLevels) {
          if (level.allow_edit && backup[level.code]) {
            const backupData = backup[level.code]
            const levelAllowEditList = normalizedLevels.filter(
              item => item.allow_edit
            )
            const newIndex = levelAllowEditList.findIndex(
              item => item.code === level.code
            )

            if (newIndex !== -1) {
              const newAssigneeType =
                newIndex < ASSIGNEE_TYPE_CONFIG.length
                  ? ASSIGNEE_TYPE_CONFIG[newIndex]
                  : `DYNAMIC_LEVEL_${newIndex}`

              // Re-select user với assigneeType mới
              await selectPeopleStore.handleSelect({
                ...backupData.userData,
                assignee_type: ASSIGNEE_TYPE.USER,
                permission: newAssigneeType,
              })
            }
          }
        }
      } catch (error) {
        console.error('Error restoring user data:', error)
      }
    },
    [selectPeopleStore, setApprovalLevels, proposalStore]
  )
  // Thêm cấp duyệt - ENHANCED VERSION
  const handleAddApprovalLevel = useCallback(async () => {
    if (approvalLevels.length >= 8) {
      message.info('Đã vượt quá số cấp duyệt cho phép.')
      return
    }

    try {
      // Backup TOÀN BỘ dữ liệu hiện tại
      const completeBackup = backupCompleteUserData()

      const newLevel = {
        status: 'WAITING',
        allow_edit: true,
        level: approvalLevels.length, // Sẽ được normalize lại từ 0
        name: `Cấp duyệt ${approvalLevels.length + 1}`, // Sẽ được normalize lại
        username: '',
        user: {
          full_name: 'Chưa gán người',
          image_id: null,
          username: '',
          department: null,
        },
        code: uuid(),
      }

      // Normalize levels để đảm bảo tuần tự
      const updatedApprovalLevels = normalizeApprovalLevels([
        ...approvalLevels,
        newLevel,
      ])

      // Restore dữ liệu ngay lập tức
      await restoreCompleteUserData(completeBackup, updatedApprovalLevels)
    } catch (error) {
      console.error('Error adding approval level:', error)
    }
  }, [approvalLevels, backupCompleteUserData, restoreCompleteUserData])

  // Xóa cấp duyệt - ENHANCED VERSION
  const handleRemoveApprovalLevel = useCallback(
    async code => {
      if (approvalLevels.length <= 2) {
        message.warning('Phải có ít nhất 2 cấp duyệt!')
        return
      }

      try {
        // Backup dữ liệu (trừ cấp sẽ bị xóa)
        const completeBackup = backupCompleteUserData()
        delete completeBackup[code] // Xóa data của cấp duyệt sẽ bị remove

        // Remove level và normalize lại
        const filteredLevels = approvalLevels.filter(
          level => level.code !== code
        )
        const updatedLevels = normalizeApprovalLevels(filteredLevels)

        // Restore dữ liệu ngay lập tức
        await restoreCompleteUserData(completeBackup, updatedLevels)
      } catch (error) {
        console.error('Error removing approval level:', error)
      }
    },
    [approvalLevels, backupCompleteUserData, restoreCompleteUserData]
  )

  const renderedFollowers = useMemo(
    () =>
      followerList.map(el => (
        <Tag
          className={'tag-selected'}
          color={'blue'}
          key={el.id}
          closable
          onClose={() => selectPeopleStore.handleRemoveSelect(el)}>
          <AvatarHasName
            imgId={el.image_id}
            size={22}
            name={el.full_name}
            icon={
              (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                <BankOutlined />
              )) ||
              (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />)
            }
          />
        </Tag>
      )),
    [followerList, selectPeopleStore]
  )

  return (
    <Form
      scrollToFirstError={true}
      form={form}
      name={'phieu-trinh-khac-form'}
      layout={'vertical'}
      onFinish={onFinish}>
      <Row type={'flex'} gutter={15}>
        <Col xs={24} lg={24}>
          <Form.Item
            name={'title'}
            label={'Tiêu đề:'}
            rules={[
              { required: true, message: 'Vui lòng nhập tiêu đề' },
              { max: 500, message: 'Tiêu đề không được dài quá 500 kí tự!' },
              { validator: validator.validateInputString },
            ]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}>
          <Form.Item
            label={
              <div>
                <p>Nội dung:</p>
              </div>
            }>
            <RichEditor
              EDITOR_REF={EDITOR_REF}
              placeholder={'Nhập nội dung'}
              editorContent={content || ''}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={10}>
          <Form.Item
            name={'date_picker'}
            label={'Thời gian:'}
            rules={[{ required: true, message: 'Vui lòng chọn thời gian' }]}>
            <RangePicker
              format={DATE_FORMAT_LIST}
              style={{ width: '100%' }}
              onChange={handleDateChange}
            />
          </Form.Item>
        </Col>
        {(proposalTypeById.id === 'LEAVE' ||
          proposalTypeById.id === 'BUSINESS_TRIP' ||
          proposalTypeById.id === 'RETIMEKEEPING' ||
          proposalTypeById.id === 'OVERTIME_NORMAL' ||
          proposalTypeById.id === 'LEAVE_ABNORMAL') &&
          proposalTimeList?.length > 0 && (
            <Col xs={24} style={{ marginBottom: '25px' }}>
              <Table
                pagination={false}
                columns={
                  proposalTypeById.id === 'LEAVE_ABNORMAL'
                    ? leaveAbnormalColumns
                    : columns
                }
                dataSource={proposalTimeList}
              />
            </Col>
          )}
        <Col xs={24} lg={24}>
          <Form.Item name={'file_ids'} label={'Tài liệu đính kèm:'}>
            <Upload
              valuePropName={'fileList'}
              fileList={fileList}
              onChange={handleChangeFile}
              beforeUpload={() => false}
              multiple={true}
              iconRender={() => <FileTextOutlined />}>
              <Button icon={<UploadOutlined />}>Chọn tài liệu đính kèm</Button>
            </Upload>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <label style={{ display: 'inline-block', margin: '15px 8px 25px 0' }}>
            Người theo dõi:
          </label>

          <Space>
            <Tooltip title="Chọn người dùng" color={blue}>
              <Button
                icon={<UserOutlined style={{ color: blue }} />}
                onClick={() =>
                  handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.FOLLOWER)
                }
              />
            </Tooltip>
          </Space>

          <SelectList>{renderedFollowers}</SelectList>
        </Col>
      </Row>
      <Divider
        style={{ width: '80%', marginBottom: '30px', fontWeight: 'bold' }}
      />
      <p>Cấp duyệt:</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Select
          showSearch
          placeholder="Chọn cấp duyệt"
          optionFilterProp="children"
          onChange={onChangeApprovalTemplate}
          filterOption={(input, option) =>
            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '300px' }}
          value={selectedApproveTemplateCode}>
          {approvalTemplateListTypeProposal &&
            approvalTemplateListTypeProposal.map(item => (
              <Select.Option key={item.code} value={item.code}>
                {item.name}
              </Select.Option>
            ))}
        </Select>
        <Button
          icon={<PlusOutlined />}
          onClick={handleAddApprovalLevel}
          style={{ marginLeft: '10px' }}>
          Thêm cấp duyệt
        </Button>
      </div>
      <ProposalStepApprove
        approvalLevelList={approvalLevels}
        styleCre={'create'}
        onRemoveApprovalLevel={handleRemoveApprovalLevel}
        isDetail={false}
        isUpdate={false}
        isCreate
      />
      <FormButtonRow>
        <Button
          onClick={() => {
            handleCloseAndReset()
          }}>
          Hủy
        </Button>
        <Button style={{ marginLeft: 10 }} htmlType={'submit'} type={'primary'}>
          Tạo mới
        </Button>
      </FormButtonRow>

      <ApprovalList />

      <SelectPeoplePopup
        noLoadSelectData
        disableSelectDepartment
        isOpenCollapse={true}
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
        isUserDisableList={false}
      />
      <SelectGroupPopup
        isVisibleSelectGroupPopup={isVisibleSelectGroupPopup}
        handleCancelSelectGroup={handleCancelSelectGroup}
        handleSubmitSelectGroup={() => setIsVisibleSelectGroupPopup(false)}
        isUserDisableList={false}
      />
    </Form>
  )
}

OtherSubmissionForm.propTypes = {}

export default memo(
  inject(
    'leavePageStore',
    'proposalStore',
    'authenticationStore',
    'fileStore',
    'loadingAnimationStore',
    'selectUserStore',
    'selectPeopleStore',
    'contentTemplateStore',
    'approvalTemplateStore'
  )(observer(OtherSubmissionForm))
)
